export const EVENTS = [
  { key: "appointment_confirmed", label: "Appointment confirmed", description: "Sent automatically when an appointment is booked. Booking still succeeds if sending is disabled or fails." },
  { key: "consent_link", label: "Consent form link", description: "Sent when a consent form is sent by WhatsApp. The mapping must include consent.url." },
];

export const VARIABLES = [
  "patient.name", "patient.phone", "patient.email", "clinic.name",
  "appointment.date", "appointment.time", "appointment.doctor_name", "appointment.service_name", "consent.url",
];
export const BIRTHDAY_EVENTS = [
  { key: "birthday", label: "Birthday - patient WhatsApp ID", template: "cusp_birthday", description: "Used when the birthday patient has a WhatsApp ID. Phone is never used as a fallback." },
  { key: "minor_birthday", label: "Birthday - parent / guardian WhatsApp ID", template: "cusp_minor_birthday", description: "Used only when the patient has no WhatsApp ID. Choose the primary eligible parent/guardian, otherwise the sole eligible contact; skip ambiguous contacts." },
];
export const BIRTHDAY_VARIABLES = ["patient.name", "patient.age", "recipient.name", "clinic.name"];

export const LIMITS = { header: 1, body: 100, buttons: 10 };
export const BUTTON_INDEXES = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
export const PREFIX_VARIABLE = "consent.url";

// Appointment events cannot use consent.url; consent events cannot use appointment.* variables.
export const variablesForEvent = (eventKey) => BIRTHDAY_EVENTS.some((event) => event.key === eventKey) ? BIRTHDAY_VARIABLES : VARIABLES.filter((v) => (
  eventKey === "consent_link" ? !v.startsWith("appointment.") : eventKey === "appointment_confirmed" ? v !== "consent.url" : true
));

export const emptyConfig = () => ({
  enabled: false, template_name: "", language_code: "en", phone_number_id: "",
  parameter_mapping: { header: [], body: [], buttons: [] },
});

export const newMapping = (eventKey) => ({ kind: "variable", value: variablesForEvent(eventKey)[0], url_prefix: "" });

const fromMapping = (m) => (m?.literal != null
  ? { kind: "literal", value: m.literal ?? "", url_prefix: "" }
  : { kind: "variable", value: m?.variable || "", url_prefix: m?.url_prefix || "" });

// API config -> editable form state. Each URL button holds exactly one parameter.
export function configToForm(config) {
  const base = emptyConfig();
  if (!config) return base;
  const pm = config.parameter_mapping || {};
  return {
    enabled: Boolean(config.enabled),
    template_name: config.template_name || "",
    language_code: config.language_code || "en",
    phone_number_id: config.phone_number_id || "",
    parameter_mapping: {
      header: (pm.header || []).map(fromMapping),
      body: (pm.body || []).map(fromMapping),
      buttons: (pm.buttons || []).map((b) => ({ index: String(b.index ?? "0"), parameter: fromMapping((b.parameters || [])[0]) })),
    },
  };
}

const toMapping = (row) => {
  if (row.kind === "literal") return { literal: row.value, type: "text" };
  const mapping = { variable: row.value, type: "text" };
  if (row.value === PREFIX_VARIABLE && row.url_prefix?.trim()) mapping.url_prefix = row.url_prefix.trim();
  return mapping;
};

// Editable form state -> API payload.
export function formToConfig(form) {
  const pm = form.parameter_mapping;
  return {
    enabled: Boolean(form.enabled),
    template_name: form.template_name.trim(),
    language_code: form.language_code.trim() || "en",
    phone_number_id: form.phone_number_id.trim() || null,
    parameter_mapping: {
      header: pm.header.map(toMapping),
      body: pm.body.map(toMapping),
      buttons: pm.buttons.map((b) => ({ index: String(b.index), sub_type: "url", parameters: [toMapping(b.parameter)] })),
    },
  };
}

// Returns a list of explicit, human-readable problems.
export function validateForm(form, eventKey) {
  const errors = [];
  const allowed = variablesForEvent(eventKey);
  const pm = form.parameter_mapping;
  if (form.enabled && !form.template_name.trim()) errors.push("Template name is required when notifications are enabled.");
  if (pm.header.length > LIMITS.header) errors.push(`Header supports at most ${LIMITS.header} parameter.`);
  if (pm.body.length > LIMITS.body) errors.push(`Body supports at most ${LIMITS.body} parameters.`);
  if (pm.buttons.length > LIMITS.buttons) errors.push(`At most ${LIMITS.buttons} URL buttons are supported.`);
  const checkRow = (row, label, allowPrefix) => {
    if (row.kind === "literal") {
      if (!row.value.trim()) errors.push(`${label}: enter a fixed value or choose a variable.`);
      return;
    }
    if (!allowed.includes(row.value)) errors.push(`${label}: ${row.value ? `"${row.value}" is not available for this event` : "choose a variable"}.`);
    const prefix = row.url_prefix?.trim();
    if (prefix && (!allowPrefix || row.value !== PREFIX_VARIABLE)) errors.push(`${label}: a URL prefix is only allowed for ${PREFIX_VARIABLE} in a URL button.`);
    else if (prefix && !/^https:\/\/\S+$/i.test(prefix)) errors.push(`${label}: URL prefix must start with https://.`);
  };
  pm.header.forEach((row, i) => checkRow(row, `Header parameter ${i + 1}`, false));
  pm.body.forEach((row, i) => checkRow(row, `Body parameter ${i + 1}`, false));
  const seen = new Set();
  pm.buttons.forEach((b, i) => {
    if (!BUTTON_INDEXES.includes(String(b.index))) errors.push(`Button ${i + 1}: index must be between 0 and 9.`);
    else if (seen.has(String(b.index))) errors.push(`Button ${i + 1}: button index ${b.index} is used more than once.`);
    seen.add(String(b.index));
    checkRow(b.parameter, `Button ${i + 1} parameter`, true);
  });
  if (eventKey === "consent_link" && form.enabled) {
    const all = [...pm.header, ...pm.body, ...pm.buttons.map((b) => b.parameter)];
    if (!all.some((r) => r.kind === "variable" && r.value === "consent.url")) errors.push("Consent link notifications must map consent.url (usually in a URL button).");
  }
  return errors;
}

export const moveItem = (list, from, to) => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};
